import { Button, Drawer, DrawerProps, message } from 'antd';
import { Formik, FormikConfig } from 'formik';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ReactComponent as Num1Svg } from '../edit/icon/1.svg';
import { ReactComponent as Num2Svg } from '../edit/icon/2.svg';
import { logisticsKnowledgeTypes, useLabel } from '@knowledge/common/hooks/useGroup';
import ConfigurationItem from '../edit/ConfigurationItem';
import KnowledgeTypeRenderer from './KnowledgeTypeRenderer';
import styled from '@emotion/styled';
import FormField from 'components/FormField';
import { newKnowledge } from '../edit/knowledge';
import {
  defaultValueMap,
  formValue2knowledgeMap,
  knowledgeTypeStepMap,
  stores2storeScopes,
  UnConfigStore,
  uniqStoreScopes,
} from 'views/BootGuide/views/AiModel/share';
import { getStoreCount } from 'components/StoreScopes';
import { DeleteOutlined } from '@ant-design/icons';
import { useEffect, useRef } from 'react';
import useModal from 'hooks/useModal';
import { logisticsKnowledgeRecreate } from '@knowledge/service/api/general';
import { useTransformKnowledges } from '../utils';
import { useTranslate } from '@tolgee/react';
import { useGetStoreGroupName } from '../StoreGroup/share';

function LogisticsEditFormDrawer({
  type,
  bottom,
  defaultValueRule,
  initialValues,
  onSubmit,
  ...otherProps
}: DrawerProps &
  Pick<FormikConfig<any>, 'initialValues' | 'onSubmit'> & {
    type: Knowledge.LogisticsKnowledgeType;
    bottom?: boolean;
    defaultValueRule?: BootGuide.AiModel.DefaultValueRule;
  }) {
  const lang = useSystemLanguageValue();

  const { t, knowledgeLabel } = useLabel();

  const { getStoreGroupName } = useGetStoreGroupName();

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bottom) {
      setTimeout(() => {
        const children = contentRef.current?.children;
        if (children) {
          children[children.length - 2]?.scrollIntoView();
        }
      });
    }
  }, [bottom]);

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
      {({ values, setValues, isSubmitting, handleSubmit }) => {
        const { storeGroup, knowledges, stores, storeMap } = values;
        return (
          <Drawer
            width={1200}
            contentWrapperStyle={{ maxWidth: '100vw' }}
            bodyStyle={{ padding: '16px', background: 'var(--fett-erp-card-bg)' }}
            title={[t('marketing.编辑'), getStoreGroupName(storeGroup)].join(
              lang === 'zh-CN' ? '' : ' ',
            )}
            footer={
              <div>
                <Button data-agent-effect="commit" type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                  {t('marketing.保存')}
                </Button>
                <Button className="ml-3" onClick={otherProps.onClose as any}>
                  {t('取消')}
                </Button>
              </div>
            }
            {...otherProps}
          >
            <div ref={contentRef} data-agent-form>
              {knowledges.map((_: any, index: number) => {
                return (
                  <FormField<any>
                    name={`knowledges.${index}`}
                    validate={(value) => {
                      const storeScopes =
                        type === 'LOGISTICS_SEND_OUT_TIME'
                          ? value.adaptValue.first.storeScopes
                          : value.adaptValue.storeScopes;
                      if (!getStoreCount(storeScopes)) {
                        return t('storeGroup.请选择店铺');
                      }
                    }}
                  >
                    {({ field: { value } }) => {
                      const storeScopes =
                        type === 'LOGISTICS_SEND_OUT_TIME'
                          ? value.adaptValue.first.storeScopes
                          : value.adaptValue.storeScopes;
                      return (
                        <Container>
                          <header>
                            <div>
                              <span>
                                <Num1Svg />
                                {t('knowledge.满足以下条件')}
                              </span>
                              {getStoreCount(storeScopes) === 0 && (
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  onClick={() =>
                                    setValues({
                                      ...values,
                                      knowledges: [
                                        ...knowledges.slice(0, index),
                                        ...knowledges.slice(index + 1),
                                      ],
                                    })
                                  }
                                >
                                  {t('marketing.删除')}
                                </Button>
                              )}
                            </div>
                            <div>
                              <span>
                                <Num2Svg />
                                {knowledgeLabel[type]}
                              </span>
                            </div>
                          </header>
                          <section>
                            <ConfigurationItem
                              fieldNamePrefix={`knowledges.${index}.condition`}
                              stores={stores}
                              storeScopesField={
                                type === 'LOGISTICS_SEND_OUT_TIME'
                                  ? {
                                      name: `knowledges.${index}.adaptValue.first.storeScopes`,
                                      storeType: 'ALL',
                                      value: value.adaptValue.first.storeScopes,
                                      onChange: (value) => {
                                        const nextStoreScopes = uniqStoreScopes(
                                          value,
                                          knowledges.map((knowledge: any) => ({
                                            storeScopes: knowledge.adaptValue.first.storeScopes,
                                          })),
                                          index,
                                        );
                                        setValues({
                                          ...values,
                                          knowledges: knowledges.map(
                                            (knowledge: any, index: number) => {
                                              return {
                                                ...knowledge,
                                                adaptValue: {
                                                  first: {
                                                    ...knowledge.adaptValue.first,
                                                    storeScopes: nextStoreScopes[index].storeScopes,
                                                  },
                                                  preSale: knowledge.adaptValue.preSale && {
                                                    ...knowledge.adaptValue.preSale,
                                                    storeScopes: nextStoreScopes[index].storeScopes,
                                                  },
                                                },
                                              };
                                            },
                                          ),
                                        });
                                      },
                                    }
                                  : {
                                      name: `knowledges.${index}.adaptValue.storeScopes`,
                                      storeType: 'ALL',
                                      value: value.adaptValue.storeScopes,
                                      onChange: (value) => {
                                        const nextStoreScopes = uniqStoreScopes(
                                          value,
                                          knowledges.map((knowledge: any) => ({
                                            storeScopes: knowledge.adaptValue.storeScopes,
                                          })),
                                          index,
                                        );
                                        setValues({
                                          ...values,
                                          knowledges: knowledges.map(
                                            (knowledge: any, index: number) => {
                                              return {
                                                ...knowledge,
                                                adaptValue: {
                                                  ...knowledge.adaptValue,
                                                  storeScopes: nextStoreScopes[index].storeScopes,
                                                },
                                              };
                                            },
                                          ),
                                        });
                                      },
                                    }
                              }
                            />
                            <div>
                              <KnowledgeTypeRenderer
                                fieldNamePrefix={`knowledges.${index}`}
                                type={type}
                                storeMap={storeMap}
                              />
                            </div>
                          </section>
                        </Container>
                      );
                    }}
                  </FormField>
                );
              })}
              <Button
                block
                ghost
                type="primary"
                className="ant-btn-dashed"
                style={{ background: 'var(--fett-bg-panel)' }}
                onClick={() => {
                  const unConfigStores = stores.filter((store: UnConfigStore) => {
                    return knowledges.every((knowledge: any) => {
                      const storeScopes: General.StoreScope[] =
                        type === 'LOGISTICS_SEND_OUT_TIME'
                          ? knowledge.adaptValue.first.storeScopes
                          : knowledge.adaptValue.storeScopes;
                      return storeScopes.every(({ regionStoreScopes }) => {
                        return regionStoreScopes.every(({ storeIds }) => {
                          return storeIds?.every((id) => id !== store.id);
                        });
                      });
                    });
                  });
                  setValues({
                    ...values,
                    knowledges: [...knowledges, createNewKnowledge(type, unConfigStores)],
                  });
                  setTimeout(() => {
                    const children = contentRef.current?.children;
                    if (children) {
                      children[children.length - 2]?.scrollIntoView();
                    }
                  });
                }}
              >
                {t('marketing.新增')}
              </Button>
            </div>
          </Drawer>
        );
      }}
    </Formik>
  );
}

export function createNewKnowledge(
  type: Knowledge.KnowledgeType,
  unConfigStores?: UnConfigStore[],
) {
  const storeScopes = stores2storeScopes(unConfigStores || []);
  const knowledge = newKnowledge();
  const adaptValue: any = {
    ...defaultValueMap[knowledgeTypeStepMap[type]],
    storeScopes,
  };
  const contentValue = formValue2knowledgeMap[type](adaptValue) || knowledge.contentValue;
  return {
    adaptValue: type === 'LOGISTICS_SEND_OUT_TIME' ? { first: adaptValue } : adaptValue,
    condition: {
      ...knowledge.conditions[0],
      ...contentValue,
      definitionCode: type,
      storeScope: {
        storeType: 'ALL',
        storeScope: storeScopes,
      },
    },
    content: contentValue.content,
    templateCode: contentValue.templateCode,
  };
}

export function useLogisticsDrawer({
  type,
  onUpdate,
}: {
  type: Knowledge.KnowledgeType;
  onUpdate: () => void;
}) {
  const { t } = useTranslate();

  const transformKnowledges = useTransformKnowledges({ type });

  const logisticsDrawerState = useModal<{ bottom?: boolean; initialValues: any }>();

  const logisticsDrawer = logisticsDrawerState.meta && logisticsKnowledgeTypes.includes(type) && (
    <LogisticsEditFormDrawer
      key={`${logisticsDrawerState.meta.initialValues.region}@@${logisticsDrawerState.meta.initialValues.storeType}`}
      type={type}
      bottom={logisticsDrawerState.meta.bottom}
      visible={logisticsDrawerState.visible}
      onClose={() => logisticsDrawerState.hide()}
      initialValues={logisticsDrawerState.meta.initialValues}
      onSubmit={(values) => {
        return logisticsKnowledgeRecreate({
          type: 'UPDATE',
          knowledges: transformKnowledges(values.knowledges).map((knowledge) => {
            return {
              ...knowledge,
              groupId: values.storeGroup.id,
            };
          }),
        })
          .then(() => {
            logisticsDrawerState.hide();
            onUpdate();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    />
  );

  return {
    t,
    transformKnowledges,
    logisticsDrawerState,
    logisticsDrawer,
  };
}

const Container = styled.div`
  border: solid 1px var(--fett-border-default);
  background: var(--fett-bg-panel);
  > header {
    display: flex;
    border-bottom: solid 1px var(--fett-border-default);
    > div {
      flex: 1 0 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 40px;
      padding: 0 16px;
      overflow: hidden;
      &:first-of-type {
        border-right: solid 1px var(--fett-border-default);
      }
      > span > svg {
        margin-right: 4px;
      }
    }
  }
  > section {
    display: flex;
    > div {
      flex: 1 0 0;
      padding: 24px;
      overflow: hidden;
      &:first-of-type {
        margin-bottom: 0;
        border: none;
        border-right: solid 1px var(--fett-border-default);
        > div {
          padding: 0;
        }
      }
    }
  }
`;

export default LogisticsEditFormDrawer;
